import { useEffect } from "react";
import { useEdgeSafeFlags } from "@/lib/resilience/edgeSafeFlags";
import { resetStadiumState } from "@/lib/pr36/stadiumVibeStateEngine";

/**
 * StadiumAmbientLayer
 * -------------------
 * Fixed background layer that paints the stadium atmosphere behind the
 * route content. State-driven via `<html data-stadium-state="...">` +
 * `--stadium-intensity` (see `stadiumVibeStateEngine`).
 *
 * Discipline:
 *   - pointer-events: none — never blocks taps.
 *   - z-index 0 with negative-stacking so route content (score, radar,
 *     overlays) always sits above.
 *   - CSS-only animations; respects prefers-reduced-motion and edgeSafe
 *     flags (allowCinematic / allowAnimatedGradients / density).
 *   - Mounted ONLY in match-aware routes (/wc/$slug, /social).
 */
export function StadiumAmbientLayer() {
  const flags = useEdgeSafeFlags();

  // Hard kill: hidden tab / freeze mode / no cinematic budget → render nothing.
  const enabled =
    flags.allowCinematic && !flags.screenshotFreezeMode && flags.density > 0.05;

  useEffect(() => {
    return () => resetStadiumState();
  }, []);

  if (!enabled) return null;

  return (
    <div
      aria-hidden
      className="stadium-ambient-layer pointer-events-none fixed inset-0 -z-10 overflow-hidden"
      data-allow-gradients={flags.allowAnimatedGradients ? "true" : "false"}
      data-allow-pulse={flags.allowPulseFX ? "true" : "false"}
    >
      <div className="stadium-aura absolute inset-0" />
      <div className="stadium-heat absolute inset-x-0 bottom-0 h-1/2" />
      <div className="stadium-tactical absolute inset-0" />
    </div>
  );
}